import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, Download, Archive, ArchiveRestore, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { DataTable, FormDialog, PageHeader, ConfirmDialog, FilterSelect, useList } from "@/components/shared";
import { api, notifyErr, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { withCargos } from "@/lib/forms";

const DEFAULT_LABELS = {
  archive: ["Archiviare l'elemento?", "Resterà consultabile con il filtro Archiviati e potrà essere ripristinato.", "Archivia"],
  restore: ["Ripristinare l'elemento?", "", "Ripristina"],
  delete: ["Eliminare definitivamente?", "Operazione irreversibile. Gli elementi collegati a storico non possono essere eliminati.", "Elimina"],
  toggle: "Archiviati",
};

export function useCrudActions(endpoint, reload, labels = DEFAULT_LABELS) {
  const [confirm, setConfirm] = useState(null);
  const run = (kind, row) => {
    const req = { archive: () => api.post(`${endpoint}/${row.id}/archive`), restore: () => api.post(`${endpoint}/${row.id}/restore`), delete: () => api.delete(`${endpoint}/${row.id}`) };
    const map = Object.fromEntries(Object.keys(req).map((k) => [k, [labels[k][0], labels[k][1], req[k]]]));
    const [title, description, fn] = map[kind];
    setConfirm({ title, description, destructive: kind === "delete", onConfirm: async () => {
      try { const r = await fn(); toast.success(r.data.message || "Operazione completata"); reload(); } catch (e) { notifyErr(e); }
    } });
  };
  const dialog = confirm && <ConfirmDialog open onOpenChange={(o) => !o && setConfirm(null)} {...confirm} />;
  return [run, dialog];
}

export default function CrudPage({ title, subtitle, endpoint, module, columns, fields, filters = [], rowLink, newLabel = "Nuovo",
  searchPlaceholder = "Cerca…", headerExtra, mapParams, initialForm, onCreated, labels = DEFAULT_LABELS, canDelete, createBlocked, onChange, steps, formTop }) {
  const [sp] = useSearchParams();
  const { can, state } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [f, setF] = useState(() => Object.fromEntries(filters.map((x) => [x.key, sp.get(x.key) || ""])));
  const [edit, setEdit] = useState(null);
  const params = { q, archived: archived ? "true" : "false", ...f, ...(mapParams ? mapParams(f) : {}) };
  const [rows, reloadList] = useList(endpoint, params);
  const reload = () => { reloadList(); onChange && onChange(); };
  const [run, dialog] = useCrudActions(endpoint, reload, labels);
  const mayDelete = canDelete ?? can(module, "delete");
  const openCreate = () => (createBlocked ? toast.error(createBlocked) : setEdit(initialForm || {}));
  useEffect(() => { if (sp.get("crea") === "1" && can(module, "create")) { openCreate(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const flds = withCargos(fields, state.features?.cargos);

  const save = async (form) => {
    if (edit?.id) { await api.put(`${endpoint}/${edit.id}`, form); toast.success("Modifiche salvate"); }
    else { const r = await api.post(endpoint, form); toast.success("Elemento creato"); onCreated && onCreated(r.data); }
    reload();
  };
  const exportParams = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        {headerExtra}
        {can(module, "export") && <>
          <Button variant="outline" onClick={() => downloadFile(`${endpoint}/export?${exportParams}&format=csv`, `${module}.csv`)} data-testid="export-csv-btn"><Download size={16} className="mr-1" />CSV</Button>
          <Button variant="outline" onClick={() => downloadFile(`${endpoint}/export?${exportParams}&format=xlsx`, `${module}.xlsx`)} data-testid="export-xlsx-btn"><Download size={16} className="mr-1" />Excel</Button>
        </>}
        {can(module, "create") && <Button className="btn-brand" onClick={openCreate} data-testid="create-btn"><Plus size={16} className="mr-1" />{newLabel}</Button>}
      </PageHeader>
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4 fade-up">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder} data-testid="list-search-input"
            className="w-full h-10 pl-9 pr-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:border-brand" />
        </div>
        {filters.map((x) => <FilterSelect key={x.key} value={f[x.key]} onChange={(v) => setF((s) => ({ ...s, [x.key]: v }))} options={x.options} placeholder={x.placeholder} testid={`filter-${x.key}`} />)}
        <label className="flex items-center gap-2 text-sm text-zinc-400 px-2"><Switch checked={archived} onCheckedChange={setArchived} data-testid="archived-toggle" />{labels.toggle}</label>
      </div>
      <DataTable columns={columns.filter((c) => !c.cargos || state.features?.cargos)} rows={rows} onRowClick={rowLink ? (r) => nav(rowLink(r)) : (can(module, "edit") ? (r) => setEdit(r) : null)}
        actions={(r) => (
          <div className="flex justify-end gap-1">
            {can(module, "edit") && <Button size="icon" variant="ghost" onClick={() => setEdit(r)} title="Modifica" data-testid={`edit-${r.id}`}><Pencil size={15} /></Button>}
            {can(module, "archive") && (r.archived
              ? <Button size="icon" variant="ghost" onClick={() => run("restore", r)} title={labels.restore[2]} data-testid={`restore-${r.id}`}><ArchiveRestore size={15} /></Button>
              : <Button size="icon" variant="ghost" onClick={() => run("archive", r)} title={labels.archive[2]} data-testid={`archive-${r.id}`}><Archive size={15} /></Button>)}
            {mayDelete && <Button size="icon" variant="ghost" onClick={() => run("delete", r)} title={labels.delete[2]} data-testid={`delete-${r.id}`}><Trash2 size={15} className="text-red-400" /></Button>}
          </div>
        )} />
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? `Modifica` : newLabel} fields={flds} initial={edit} onSubmit={save} steps={steps} top={formTop} />
      {dialog}
    </div>
  );
}
